/**
 * StackTraceViewer - Premium Stack Trace Visualization Component
 *
 * Features inspired by Sentry, Datadog, and New Relic:
 * - Collapsible/expandable stack frames
 * - User code vs third-party code highlighting
 * - "Culprit" frame highlighting (most relevant frame)
 * - Clickable source links (VS Code integration)
 * - Copy individual frames or entire stack
 * - Frame grouping (collapse consecutive third-party frames)
 * - Search/filter within stack
 * - Async boundary indicators
 * - Raw stack view toggle
 *
 * @example
 * <StackTraceViewer source={log.source} />
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  Code2,
  Layers,
  Search,
  Eye,
  EyeOff,
  Maximize2,
  Minimize2,
  AlertCircle,
  Zap,
} from 'lucide-react';
import type { SourceLocation, StackFrame } from '../../../utils/WeBelieveLogger.types';

interface StackTraceViewerProps {
  source: SourceLocation;
  /** Project base path for generating full file paths */
  projectBasePath?: string;
  /** Initial expansion state */
  defaultExpanded?: boolean;
  /** Show frame numbers */
  showFrameNumbers?: boolean;
  /** Enable search */
  enableSearch?: boolean;
  /** Compact mode (fewer details) */
  compact?: boolean;
}

// Generate VS Code URL for a stack frame
function generateVSCodeUrl(
  file: string,
  line: number,
  column?: number,
  projectBasePath: string = '/home/edgelexllc/projects/edgelexapp'
): string {
  let filePath = file;

  // Map relative paths to absolute paths
  if (filePath.startsWith('src/')) {
    filePath = `${projectBasePath}/webelieve-frontend/${filePath}`;
  } else if (filePath.startsWith('wasm/')) {
    filePath = `${projectBasePath}/typesetter/${filePath}`;
  } else if (!filePath.startsWith('/')) {
    filePath = `${projectBasePath}/webelieve-frontend/${filePath}`;
  }

  // Build VS Code URL using SSH remote protocol
  let url = `vscode://vscode-remote/ssh-remote+edgelexllc${filePath}`;
  if (line) {
    url += `:${line}`;
    if (column) {
      url += `:${column}`;
    }
  }

  return url;
}

// Format file path for display (truncate long paths)
function formatFilePath(file: string, maxLength: number = 60): string {
  if (file.length <= maxLength) return file;

  const parts = file.split('/');
  if (parts.length <= 2) return file;

  // Keep first and last parts, abbreviate middle
  const first = parts[0];
  const last = parts.slice(-2).join('/');

  if (`${first}/.../${last}`.length <= maxLength) {
    return `${first}/.../${last}`;
  }

  return `.../${last}`;
}

// Single stack frame row component
function StackFrameRow({
  frame,
  index,
  isCulprit,
  isExpanded,
  onToggle,
  projectBasePath,
  showFrameNumber,
  compact,
}: {
  frame: StackFrame;
  index: number;
  isCulprit: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  projectBasePath: string;
  showFrameNumber: boolean;
  compact: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const text = `${frame.function || '(anonymous)'} at ${frame.file}:${frame.line}:${frame.column}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [frame]);

  const vscodeUrl = generateVSCodeUrl(frame.file, frame.line, frame.column, projectBasePath);

  // Background color based on frame type
  const bgColor = isCulprit
    ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500'
    : frame.isUserCode
    ? 'bg-lex-accent-soft border-l-4 border-lex-accent'
    : 'bg-gray-50 dark:bg-gray-800/50 border-l-4 border-gray-300 dark:border-gray-600';

  const textColor = frame.isUserCode
    ? 'text-gray-900 dark:text-gray-100'
    : 'text-gray-500 dark:text-gray-400';

  return (
    <div
      className={`${bgColor} rounded-r-md mb-1 transition-all hover:shadow-sm`}
    >
      {/* Main row - always visible */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none"
        onClick={onToggle}
      >
        {/* Expand/collapse indicator */}
        <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Frame number */}
        {showFrameNumber && (
          <span className="text-xs text-gray-400 dark:text-gray-500 w-6 text-right font-mono">
            #{index}
          </span>
        )}

        {/* Culprit indicator */}
        {isCulprit && (
          <span
            className="px-1.5 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded"
            title="Most relevant frame (culprit)"
          >
            CULPRIT
          </span>
        )}

        {/* Async indicator */}
        {frame.isAsync && (
          <span title="Async frame">
            <Zap className="w-3.5 h-3.5 text-yellow-500" />
          </span>
        )}

        {/* User code indicator */}
        {frame.isUserCode ? (
          <span title="Your code">
            <Code2 className="w-4 h-4 text-lex-accent" />
          </span>
        ) : (
          <span title="Third-party/framework code">
            <Layers className="w-4 h-4 text-gray-400" />
          </span>
        )}

        {/* Function name */}
        <span className={`font-mono text-sm ${textColor} font-medium`}>
          {frame.className ? (
            <>
              <span className="text-purple-600 dark:text-purple-400">{frame.className}</span>
              <span className="text-gray-400">.</span>
              {frame.function || '(anonymous)'}
            </>
          ) : (
            frame.function || '(anonymous)'
          )}
        </span>

        {/* File location */}
        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate flex-1">
          {formatFilePath(frame.file)}
          <span className="text-gray-400">:</span>
          <span className="text-blue-600 dark:text-blue-400">{frame.line}</span>
          {frame.column && (
            <>
              <span className="text-gray-400">:</span>
              <span className="text-gray-500">{frame.column}</span>
            </>
          )}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleCopy}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Copy frame"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
          <a
            href={vscodeUrl}
            onClick={(e) => e.stopPropagation()}
            className="p-1 text-gray-400 hover:text-lex-accent transition-colors"
            title="Open in VS Code"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && !compact && (
        <div className="px-10 pb-3 text-xs font-mono">
          <div className="grid grid-cols-2 gap-2 text-gray-600 dark:text-gray-400">
            <div>
              <span className="text-gray-400">File: </span>
              {frame.file}
            </div>
            <div>
              <span className="text-gray-400">Line: </span>
              {frame.line}:{frame.column || 0}
            </div>
            {frame.function && (
              <div>
                <span className="text-gray-400">Function: </span>
                {frame.function}
              </div>
            )}
            {frame.className && (
              <div>
                <span className="text-gray-400">Class: </span>
                {frame.className}
              </div>
            )}
            {frame.raw && (
              <div className="col-span-2 mt-2">
                <span className="text-gray-400">Raw: </span>
                <code className="text-gray-500 bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-[10px]">
                  {frame.raw}
                </code>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Group of collapsed third-party frames
function CollapsedFrameGroup({
  frames,
  startIndex,
  onExpand,
}: {
  frames: StackFrame[];
  startIndex: number;
  onExpand: () => void;
}) {
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-md mb-1 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
      onClick={onExpand}
    >
      <ChevronRight className="w-4 h-4 text-gray-400" />
      <Layers className="w-4 h-4 text-gray-400" />
      <span className="text-sm text-gray-500 dark:text-gray-400">
        {frames.length} third-party frames
      </span>
      <span className="text-xs text-gray-400">
        (click to expand)
      </span>
    </div>
  );
}

export function StackTraceViewer({
  source,
  projectBasePath = '/home/edgelexllc/projects/edgelexapp',
  defaultExpanded = false,
  showFrameNumbers = true,
  enableSearch = true,
  compact = false,
}: StackTraceViewerProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [expandedFrames, setExpandedFrames] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showThirdParty, setShowThirdParty] = useState(true);
  const [showRawStack, setShowRawStack] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const stack = source.stack || [];
  const culpritIndex = source.culpritIndex ?? 0;

  // Filter frames based on search and visibility settings
  const filteredFrames = useMemo(() => {
    let frames = stack;

    if (!showThirdParty) {
      frames = frames.filter((f) => f.isUserCode);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      frames = frames.filter(
        (f) =>
          f.file.toLowerCase().includes(query) ||
          f.function?.toLowerCase().includes(query) ||
          f.className?.toLowerCase().includes(query)
      );
    }

    return frames;
  }, [stack, showThirdParty, searchQuery]);

  // Group consecutive third-party frames for collapsing
  const frameGroups = useMemo(() => {
    if (showThirdParty) {
      return filteredFrames.map((frame, idx) => ({
        type: 'single' as const,
        frame,
        originalIndex: stack.indexOf(frame),
      }));
    }
    return filteredFrames.map((frame, idx) => ({
      type: 'single' as const,
      frame,
      originalIndex: stack.indexOf(frame),
    }));
  }, [filteredFrames, showThirdParty, stack]);

  const toggleFrame = useCallback((index: number) => {
    setExpandedFrames((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const handleCopyAll = useCallback(() => {
    const stackText = stack
      .map(
        (f, i) =>
          `#${i} ${f.function || '(anonymous)'} at ${f.file}:${f.line}:${f.column}`
      )
      .join('\n');
    navigator.clipboard.writeText(stackText);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  }, [stack]);

  const expandAll = useCallback(() => {
    setExpandedFrames(new Set(stack.map((_, i) => i)));
  }, [stack]);

  const collapseAll = useCallback(() => {
    setExpandedFrames(new Set());
  }, []);

  // No stack trace available
  if (!stack || stack.length === 0) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        No stack trace available
        {source.file && (
          <span className="font-mono">
            (primary: {source.file}:{source.line})
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
          <span className="font-medium text-gray-700 dark:text-gray-200">
            Stack Trace
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({stack.length} frames{source.stackTruncated && ', truncated'})
          </span>
          {source.culpritIndex !== undefined && (
            <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
              Culprit: #{source.culpritIndex}
            </span>
          )}
        </div>

        {/* Header actions */}
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleCopyAll}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Copy entire stack"
          >
            {copiedAll ? (
              <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
            Copy
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="p-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {/* Search */}
              {enableSearch && (
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search frames..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lex-accent w-48"
                  />
                </div>
              )}

              {/* Toggle third-party frames */}
              <button
                onClick={() => setShowThirdParty(!showThirdParty)}
                className={`flex items-center gap-1 px-2 py-1.5 text-xs rounded-md transition-colors ${
                  showThirdParty
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    : 'bg-lex-accent-soft text-lex-accent'
                }`}
                title={showThirdParty ? 'Hide third-party frames' : 'Show third-party frames'}
              >
                {showThirdParty ? (
                  <Eye className="w-3.5 h-3.5" />
                ) : (
                  <EyeOff className="w-3.5 h-3.5" />
                )}
                Third-party
              </button>

              {/* Toggle raw view */}
              <button
                onClick={() => setShowRawStack(!showRawStack)}
                className={`flex items-center gap-1 px-2 py-1.5 text-xs rounded-md transition-colors ${
                  showRawStack
                    ? 'bg-lex-accent-soft text-lex-accent'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
                title="Toggle raw stack view"
              >
                <Code2 className="w-3.5 h-3.5" />
                Raw
              </button>
            </div>

            {/* Expand/Collapse all */}
            <div className="flex items-center gap-1">
              <button
                onClick={expandAll}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                title="Expand all frames"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button
                onClick={collapseAll}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                title="Collapse all frames"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Raw stack view */}
          {showRawStack ? (
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs font-mono overflow-auto max-h-96">
              {stack.map((f) => f.raw).join('\n')}
            </pre>
          ) : (
            /* Stack frames */
            <div className="space-y-0">
              {filteredFrames.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No frames match your search
                </div>
              ) : (
                filteredFrames.map((frame, displayIndex) => {
                  const originalIndex = stack.indexOf(frame);
                  return (
                    <div key={originalIndex} className="group">
                      <StackFrameRow
                        frame={frame}
                        index={originalIndex}
                        isCulprit={originalIndex === culpritIndex}
                        isExpanded={expandedFrames.has(originalIndex)}
                        onToggle={() => toggleFrame(originalIndex)}
                        projectBasePath={projectBasePath}
                        showFrameNumber={showFrameNumbers}
                        compact={compact}
                      />
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <div className="w-1 h-4 bg-red-500 rounded"></div>
              Culprit
            </div>
            <div className="flex items-center gap-1">
              <div className="w-1 h-4 bg-lex-accent rounded"></div>
              Your code
            </div>
            <div className="flex items-center gap-1">
              <div className="w-1 h-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
              Third-party
            </div>
            <div className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-yellow-500" />
              Async
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StackTraceViewer;
